import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Form,
  Row,
  Col,
  Container,
  Alert,
  Badge,
  Button,
  Label,
  Input,
} from 'reactstrap';

import FormInput from '@/components/forms/FormInput';
import { AdminAgentInfoDto, AdminRegionInfoDto, CreateAgentDealDto, DealType } from '@repo/common-types';
import { fetchUsersRest } from '@/services/users/users-rest';
import { UserInfo } from '@/services/users/user-types';
import FormAsyncSelect from '@/components/forms/FormAsyncSelect';

// User selection option type for AsyncSelect
type UserSelectOption = {
  value: string; // userId
  label: string; // "alias - email"
  user: UserInfo;
};

type RegionSelectOption = {
  value: string; // regionId
  label: string; // "Region Name"
  region: AdminRegionInfoDto;
};

const validationSchema = Yup.object().shape({
  // CreateAgentDealDto fields
  user: Yup.mixed<UserSelectOption>().required('User must be selected'),
  dealType: Yup.mixed<DealType>()
    .oneOf([DealType.OVERRIDE, DealType.FLAT], 'Deal type must be OVERRIDE or FLAT')
    .required('Deal type is required'),
  rakeSharePercentage: Yup.number()
    .transform((value) => (isNaN(value) || value === '' ? undefined : value))
    .min(5, 'Minimum rake share is 5%')
    .max(45, 'Maximum rake share is 45%')
    .required('Rake share percentage is required'),
  flatFee: Yup.number()
    .nullable()
    .when('dealType', {
      is: DealType.FLAT,
      then: (schema) =>
        schema
          .min(0.01, 'Flat fee agents must have a flat fee greater than 0')
          .required('Flat fee is required for Flat deals'),
      otherwise: (schema) => schema.nullable(),
    }),
  bonus: Yup.number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .min(0, 'Bonus must be positive'),
  startDate: Yup.date()
    .transform((value) => {
      // Handle case where value might be an array or string from datetime-local input
      if (Array.isArray(value)) {
        return value[0] ? new Date(value[0]) : undefined;
      }
      if (typeof value === 'string') {
        return value ? new Date(value) : undefined;
      }
      return value;
    })
    .required('Start date is required'),
  endDate: Yup.date()
    .transform((value) => {
      // Handle case where value might be an array or string from datetime-local input
      if (Array.isArray(value)) {
        return value[0] ? new Date(value[0]) : undefined;
      }
      if (typeof value === 'string') {
        return value ? new Date(value) : undefined;
      }
      return value;
    })
    .required('End date is required')
    .test('is-after-start', 'End date must be after start date', function (value) {
      const { startDate } = this.parent;
      if (startDate && value && value <= startDate) {
        return false;
      }
      return true;
    }),
  isRegionalManager: Yup.boolean().required(),
  automaticDownlineInheritance: Yup.boolean().required(),
  completeUserVisibility: Yup.boolean().required(),
  region: Yup.mixed<RegionSelectOption>()
    .nullable()
    .when('isRegionalManager', {
      is: true,
      then: (schema) => schema.required('Region must be selected for regional managers'),
      otherwise: (schema) => schema.nullable(),
    }),
});

type AgentDealFormData = Yup.InferType<typeof validationSchema>;

interface AgentDealFormModalProps {
  onClose: () => void;
  onSubmit: (data: CreateAgentDealDto) => void;
  agent?: AdminAgentInfoDto; // If provided, user is preselected (edit/upgrade). If not provided, user selection required (create)
  isLoading?: boolean;
}

/**
 * Agent Deal Form Modal
 * Comprehensive form for creating, editing, and upgrading agent deals
 * Includes all fields specified in the task requirements with proper validation
 */
const AgentDealFormModal: React.FC<AgentDealFormModalProps> = ({ onClose, onSubmit, agent, isLoading = false }) => {
  // Load user options for AsyncSelect
  const loadUserOptions = async (inputValue: string): Promise<UserSelectOption[]> => {
    if (inputValue.length < 2) return [];

    try {
      const result = await fetchUsersRest({
        skip: 0,
        take: 25,
        filter: {
          alias: inputValue,
        },
      });

      return result.users.map((user) => ({
        value: user.id,
        label: `${user.alias} - ${user.email}`,
        user,
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      return [];
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<AgentDealFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      // CreateAgentDealDto fields
      user: agent?.id
        ? { value: agent.id, label: `${agent.alias} - ${agent.email}`, user: agent as unknown as UserInfo }
        : undefined,
      dealType: agent?.agentDeal?.dealType || DealType.OVERRIDE,
      rakeSharePercentage:
        agent?.agentDeal?.rakeSharePercentage && agent?.agentDeal?.rakeSharePercentage < 1
          ? agent?.agentDeal?.rakeSharePercentage * 100
          : agent?.agentDeal?.rakeSharePercentage || undefined,
      flatFee: agent?.agentDeal?.flatFee || null,
      bonus: null,
      isRegionalManager: agent?.agentDeal?.isRegionalManager || false,
      region: null,
      automaticDownlineInheritance: agent?.agentDeal?.automaticDownlineInheritance || false,
      completeUserVisibility: agent?.agentDeal?.completeUserVisibility || false,
      startDate: agent?.agentDeal?.startDate ? new Date(agent.agentDeal.startDate) : undefined,
      endDate: agent?.agentDeal?.endDate ? new Date(agent.agentDeal.endDate) : undefined,
    },
    mode: 'onChange',
  });

  const watchedDealType = watch('dealType');

  const getModalTitle = () => {
    if (!agent) {
      return 'Create Agent Deal';
    }
    return agent.hasAgentDeal ? 'Edit Agent Deal' : 'Create Agent Deal';
  };

  const handleFormSubmit = (data: AgentDealFormData) => {
    const { user, region, ...rest } = data;
    // transform the data to CreateAgentDealDto
    const createAgentDealDto: CreateAgentDealDto = {
      ...rest,
      userId: user.value,
      regionId: region?.value,
    };
    // Data already matches AgentDealFormData structure (CreateAgentDealDto + UI fields)
    onSubmit(createAgentDealDto);
  };

  const handleModalClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={true} toggle={handleModalClose} size="lg" centered className="agent-deal-modal">
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalHeader toggle={handleModalClose} className="bg-dark p-4">
          {getModalTitle()}
        </ModalHeader>

        <ModalBody className="p-4">
          <Container fluid className="d-flex flex-column gap-4">
            {/* User Selection Section */}
            <Row className="g-4">
              <Col md={12}>
                {agent ? (
                  <>
                    <Label className="form-label">User</Label>
                    <Input
                      type="text"
                      disabled
                      style={{ cursor: 'not-allowed' }}
                      value={`${agent.alias} - ${agent.email}`}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      name="user"
                      control={control}
                      render={({ field }) => (
                        <FormAsyncSelect
                          {...field}
                          loadOptions={loadUserOptions}
                          placeholder="Search for a user by alias..."
                          label="Select User"
                        />
                      )}
                    />
                  </>
                )}
              </Col>
            </Row>

            {/* Current UserAgreement Information (when agent has existing agreement but no deal) */}
            {agent && !agent.hasAgentDeal && agent.currentUserAgreement && (
              <Alert color="info" className="mb-4">
                <h6>Current Agreement Status</h6>
                <p className="mb-2">
                  <strong>Current UserAgreement:</strong> {agent.currentUserAgreement.type}
                  <Badge color={agent.currentUserAgreement.isActive ? 'success' : 'warning'} className="ms-2">
                    {agent.currentUserAgreement.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </p>
                <p className="mb-0">
                  <small>
                    Creating an Agent Deal will deactivate the current UserAgreement and create a new{' '}
                    <code>agent_deal_v3</code> agreement. The Agent Deal will take precedence in rake calculations.
                  </small>
                </p>
              </Alert>
            )}

            {/* // Add line separator */}
            <hr className="text-body-secondary m-0" />
            {/* Deal Configuration Section */}

            <Row className="g-4">
              {/* Deal Type Selection */}
              <Col xs={12}>
                <label className="form-label">Deal Type</label>
                <div className="d-flex gap-3">
                  <Controller
                    name="dealType"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="dealType-flat"
                            value="FLAT"
                            checked={field.value === 'FLAT'}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="dealType-flat">
                            Flat
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="dealType-override"
                            value="OVERRIDE"
                            checked={field.value === 'OVERRIDE'}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="dealType-override">
                            Override
                          </label>
                        </div>
                      </>
                    )}
                  />
                </div>
              </Col>

              {/* Rake Share Percentage */}
              <Col md={6}>
                <label className="form-label">Rake Share %</label>
                <FormInput
                  name="rakeSharePercentage"
                  control={control}
                  type="number"
                  min={5}
                  max={45}
                  placeholder="5% - 45%"
                />
              </Col>

              {/* Flat Fee (only for Flat deals) */}
              {watchedDealType === DealType.FLAT && (
                <Col md={6}>
                  <label className="form-label">Flat Fee ($)</label>
                  <FormInput name="flatFee" control={control} type="number" min={0} />
                </Col>
              )}
            </Row>

            {/* // Add line separator */}
            <hr className="text-body-secondary m-0" />

            {/* Deal Period Configuration */}
            <Row className="g-4">
              <Col md={6}>
                <label className="form-label">Start Date</label>
                <FormInput name="startDate" control={control} type="datetime-local" />
              </Col>
              <Col md={6}>
                <label className="form-label">End Date</label>
                <FormInput name="endDate" control={control} type="datetime-local" />
              </Col>
            </Row>
          </Container>
        </ModalBody>

        <ModalFooter className="border-top p-4">
          <Button type="button" outline color="dark" onClick={handleModalClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading || !isValid || !isDirty}>
            {isLoading ? 'Saving...' : agent?.hasAgentDeal ? 'Update Deal' : 'Create Deal'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AgentDealFormModal;
